import { Form, Formik, FormikProps } from 'formik';
import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useAsyncError } from '../../common/error/useAsyncError';
import { ConfigContext } from '../configuration/ConfigContext';
import Button from '../forms/Button';
import MaskedTextField from '../forms/MaskedTextField';
import RadioButton from '../forms/RadioButton';
import RadioButtonGroup from '../forms/RadioButtonGroup';
import { swishMask } from '../forms/masks';
import PaperLayout from '../layout/PaperLayout';
import OverlayConfirmModal from '../modal/OverlayConfirmModal';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { ToastContext } from '../toaster/ToastContext';
import { updatePropertyAssociationSettings, usePropertyAssociationSettings } from './settingsService';

type FormData = {
    hasSwish: string;
    hasChosenRiksbyggen: string;
    swishNumber: string;
};

const swishRegExp = /^(\d{3} ?\d{3} ?\d{2} ?\d{2})$/;

const validationSchema = Yup.object().shape({
    swishNumber: Yup.string().required('Swish-nummer måste anges').matches(swishRegExp, 'Ogiltigt Swish-nummer (ska vara "123" följt av sju siffror)'),
});

const PREAMBLE = (
    <>
        <p>
            För att kunna erbjuda Swish som betalmetod vid bokning av gemensamma resurser behöver din förening ha ett Swish Handelsavtal med banken. Det ansöker ni om hos er bank och där väljer ni
            Riksbyggen som teknisk leverantör. Hos de flesta banker ansöker man om Swish Handel digitalt, för att göra det behöver du tjänsten internetbank hos din bank.
        </p>
        <p>
            Med Swish aktiverat som betalsätt kan era medlemmar betala med Swish direkt efter att bokningen är nyttjad. Dagen efter får medlemmen en notis om att det nu går att betala bokningen via
            Swish. Det går fortfarande bra att avvakta och få betalningen på sin månadsavi.
        </p>
    </>
);

type SwishAction = 'enable' | 'terminate';

const SwishSettings: FunctionComponent = () => {
    const throwAsync = useAsyncError();
    const { apiBaseUrl } = useContext(ConfigContext);
    const { addToast } = useContext(ToastContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();
    const { settings, error, refetch } = usePropertyAssociationSettings();
    const [initialValues, setInitialValues] = useState<FormData>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [lastSubmittedAction, setLastSubmittedAction] = useState<SwishAction | null>(null);
    const formikRef = useRef<FormikProps<any>>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        if (settings) {
            if (settings.provideSwishPayment && settings.swishNumber) {
                setInitialValues({
                    hasSwish: 'yes',
                    hasChosenRiksbyggen: 'yes',
                    swishNumber: settings.swishNumber,
                });
            } else {
                setInitialValues({
                    hasSwish: '',
                    hasChosenRiksbyggen: '',
                    swishNumber: '123 ',
                });
            }
        }
    }, [settings]);

    useEffect(() => {
        if (error) {
            throwAsync(new Error('Det gick inte att hämta inställningarna'));
        }
    }, [error]);

    if (!initialValues) {
        return null;
    }

    const handleSubmit = (values) => {
        const settings = {
            provideSwishPayment: true,
            swishNumber: values.swishNumber,
        };

        setIsSubmitting(true);
        updatePropertyAssociationSettings(
            apiBaseUrl,
            currentPropertyAssociationId,
            settings,
            () => onSubmitted(),
            () => addToast('Det gick inte att uppdatera inställningarna'),
        );
    };

    const endSwishPayments = () => {
        const settings = {
            provideSwishPayment: false,
            swishNumber: null,
        };

        setIsSubmitting(true);
        updatePropertyAssociationSettings(
            apiBaseUrl,
            currentPropertyAssociationId,
            settings,
            () => onSubmitted('Swish-betalningar avslutade', 'terminate'),
            () => addToast('Det gick inte att avsluta Swish-betalningar'),
        );
    };

    const onSubmitted = (toastMessage = 'Swish-inställningar sparade', action: SwishAction = 'enable') => {
        console.log({ toastMessage });
        addToast(toastMessage);
        setIsSubmitted(true);
        setLastSubmittedAction(action);
        setIsSubmitting(false);
        refetch();
    };

    const disabledRadioButtons = isSubmitting || initialValues.hasSwish === 'yes';

    return (
        <>
            <Formik initialValues={initialValues} innerRef={formikRef} enableReinitialize={true} validationSchema={validationSchema} validateOnMount={true} onSubmit={(values) => handleSubmit(values)}>
                {({ errors, isValid, touched, values }) => (
                    <Form>
                        <PaperLayout heading="Swish" preamble={PREAMBLE} mainClass="pb-5 pt-1" tightenUp={true}>
                            {isSubmitted && lastSubmittedAction === 'enable' && (
                                <div className="alert alert-success mb-4">
                                    Nu är ert Swish-nummer sparat och redo att användas för betalning. Efter genomförd bokning kommer boende få alternativet att betala med Swish.
                                </div>
                            )}

                            {isSubmitted && lastSubmittedAction === 'terminate' && (
                                <div className="alert alert-success mb-4">
                                    Swish-betalningar är nu avslutade. Boende kommer inte längre att få möjlighet att välja Swish som betalsätt vid bokning. Om ni inte vill ha Swish alls i föreningen
                                    behöver ni också säga upp avtalet ni har med banken.
                                </div>
                            )}

                            <div className="mb-4">
                                <h2>Har du Swish Handel idag och valt Riksbyggen som teknisk leverantör?</h2>
                                <RadioButtonGroup name="hasSwish" className="d-flex gap-5" errors={errors} touched={touched}>
                                    <RadioButton name="hasSwish" value="yes" label="Ja" readonly={disabledRadioButtons} />
                                    <RadioButton name="hasSwish" value="no" label="Nej" readonly={disabledRadioButtons} />
                                </RadioButtonGroup>
                            </div>

                            {values.hasSwish === 'yes' && (
                                <>
                                    <div className="mb-4">
                                        <h2>Har du fått bekräftelse från banken att Swish Handel är aktiverat?</h2>
                                        <RadioButtonGroup name="hasChosenRiksbyggen" className="d-flex gap-5" errors={errors} touched={touched}>
                                            <RadioButton name="hasChosenRiksbyggen" value="yes" label="Ja" readonly={disabledRadioButtons} />
                                            <RadioButton name="hasChosenRiksbyggen" value="no" label="Nej" readonly={disabledRadioButtons} />
                                        </RadioButtonGroup>
                                    </div>

                                    {values.hasChosenRiksbyggen === 'yes' && (
                                        <>
                                            <div className="mb-4">
                                                <h2>Skriv in ditt Swish-nummer</h2>
                                                <p className="mb-3">Skriv in ditt Swish-nummer (123-nummer) som du har fått av banken när du angav Riksbyggen som din tekniska leverantör</p>
                                                <MaskedTextField
                                                    name="swishNumber"
                                                    label="Swish-nummer"
                                                    placeholder="123 XXX XX XX"
                                                    mask={swishMask}
                                                    extraClass="swish-field__input"
                                                    errors={errors}
                                                    touched={touched}
                                                    values={values}
                                                />
                                            </div>

                                            <div className="d-flex flex-wrap justify-content-between gap-3 flex-row-reverse">
                                                <Button type="submit" variant="success" text="Spara Swish-nummer" disabled={!isValid || isSubmitting} />
                                                {settings?.provideSwishPayment && (
                                                    <Button type="button" text="Avsluta Swish-betalningar" disabled={isSubmitting} onClick={() => setShowConfirmModal(true)} />
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {values.hasChosenRiksbyggen === 'no' && (
                                        <>
                                            <h2>Invänta bekräftelse från banken</h2>
                                            <p>
                                                För att kunna använda Swish Handel behöver du först få en bekräftelse från din bank att tjänsten är aktiverad. Vänligen kontakta din bank om du har
                                                några frågor kring aktiveringen.
                                            </p>
                                        </>
                                    )}
                                </>
                            )}

                            {values.hasSwish === 'no' && (
                                <>
                                    <h2>Kom igång med Swish Handel</h2>
                                    <p className="mb-2">
                                        Innan du kan använda Swish Handel behöver du ange Riksbyggen som din tekniska leverantör och få ett Swish-nummer. Aktivering och hantering av Swish Handel sker
                                        via din bank, och du hittar mer information på respektive banks hemsida:
                                    </p>
                                    <ul className="mb-4">
                                        <li>
                                            <a href="https://www.swedbank.se/foretag/betala-och-ta-betalt/swish/swish-handel.html" target="_blank" rel="noreferrer">
                                                Swedbank
                                            </a>
                                            <ul>
                                                <li>
                                                    <a href="/files/ny-swish-handel-kund-riksbyggen.pdf" target="_blank" rel="noreferrer">
                                                        Lathund Swish Handel - Swedbank
                                                    </a>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            <a href="https://www.handelsbanken.se/sv/foretag/konton-betalningar/ta-betalt/swish-for-foretag" target="_blank" rel="noreferrer">
                                                Handelsbanken
                                            </a>
                                        </li>
                                        <li>
                                            <a href="https://www.nordea.se/foretag/produkter/betala/swish-foretag.html" target="_blank" rel="noreferrer">
                                                Nordea
                                            </a>
                                        </li>
                                    </ul>
                                </>
                            )}

                            {error && <div className="alert alert-danger mt-5">{error}</div>}
                        </PaperLayout>
                    </Form>
                )}
            </Formik>
            <OverlayConfirmModal
                heading="Är du säker på att du vill avsluta Swish-betalningar"
                text="Genom att avsluta Swish så tas möjligheten bort för boende att välja Swish som betalsätt vid gjorda bokningar. Om ni inte vill ha Swish alls i föreningen behöver ni också säga upp avtalet ni har med banken."
                visible={showConfirmModal}
                okButtonText="Avsluta"
                cancelButtonText="Avbryt"
                onCancel={() => setShowConfirmModal(false)}
                onSubmit={() => {
                    setShowConfirmModal(false);
                    console.log(formikRef.current);
                    if (formikRef.current) {
                        endSwishPayments();
                    }
                }}
                okButtonVariant="primary"
            />
        </>
    );
};

export default SwishSettings;
